import type {
  EventBasedMarketView,
  EventId,
  EventTypeView,
  MarketId,
  MeetingBasedEventView
} from '@ps/isp-web-services-types';
import type { Selector } from 'react-redux';

import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  RootState,
  SelectorConfig,
  SelectorCreator,
  Slicer,
  WidgetModelConfig
} from '@swf/core/store';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import type { EventTypeId } from '@swf/sports/types/eventType';

import type { AntePostBasedMarketTemplatesView } from '../../types/templates';

export type EventsSelector = Selector<RootState, AntePostBasedMarketTemplatesView['events']>;
export type EventTypesSelector = Selector<
  RootState,
  AntePostBasedMarketTemplatesView['eventTypes']
>;
export type MarketsSelector = Selector<RootState, AntePostBasedMarketTemplatesView['markets']>;

const initialState: AntePostBasedMarketTemplatesView = {
  events: {},
  eventTypes: {},
  markets: {}
};

export const selectors = (
  slice: Slicer<AntePostBasedMarketTemplatesView>,
  createSelector: SelectorCreator
) => ({
  events: (): EventsSelector => slice((state) => state.events),
  event(this: { events: EventsSelector }) {
    return createSelector(
      this.events,
      (_state: RootState, eventId: EventId) => eventId,
      (events, eventId): MeetingBasedEventView => events[eventId]
    );
  },
  eventTypes: (): EventTypesSelector => slice((state) => state.eventTypes),
  eventType(this: { eventTypes: EventTypesSelector }) {
    return createSelector(
      this.eventTypes,
      (_state: RootState, eventTypeId: EventTypeId) => eventTypeId,
      (eventTypes, eventTypeId): EventTypeView => eventTypes[eventTypeId] as EventTypeView
    );
  },
  markets: (): MarketsSelector => slice((state) => state.markets),
  market(this: { markets: MarketsSelector }) {
    return createSelector(
      this.markets,
      (_state: RootState, marketId: MarketId) => marketId,
      (markets, marketId): EventBasedMarketView | undefined => markets[marketId]
    );
  },
  marketIds(this: { markets: MarketsSelector }) {
    return createSelector(this.markets, (markets) => Object.keys(markets));
  },
  marketTemplateData(this: {
    events: EventsSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.events,
      this.eventTypes,
      this.markets,
      (events, eventTypes, markets): AntePostBasedMarketTemplatesView => ({
        events,
        eventTypes,
        markets
      })
    );
  },
  /**
   * @deprecated GSFE-2137 - use "marketTemplateData" instead
   */
  getMarketTemplateData(this: {
    events: EventsSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.events,
      this.eventTypes,
      this.markets,
      (events, eventTypes, markets): AntePostBasedMarketTemplatesView => ({
        events,
        eventTypes,
        markets
      })
    );
  }
});

export const withAntePostBasedMarketTemplateModelState = <T>(
  modelState: T
): T & AntePostBasedMarketTemplatesView => ({ ...initialState, ...modelState });

export function withAntePostBasedMarketTemplateModelSelectors<
  S extends AntePostBasedMarketTemplatesView,
  F extends (slice: Slicer<S>, createSelector: SelectorCreator) => SelectorConfig<S>
>(_state: S, modelSelectorFactory: F) {
  return (slice: Slicer<S>, createSelector: SelectorCreator) =>
    ({
      ...selectors(slice, createSelector),
      ...modelSelectorFactory(slice, createSelector)
    } as ReturnType<typeof selectors> & ReturnType<F>);
}

export interface AntePostMarketTemplatesModel
  extends WidgetModelConfig<BasicSportsWidgetConfig, AntePostBasedMarketTemplatesView> {
  selectors: typeof selectors;
}

export const useAntePostMarketTemplatesWidget: UseWidget<
  BasicSportsWidgetConfig,
  AntePostMarketTemplatesModel
> = useWidget;
